import { Filter, RefreshCw, Inbox, Loader2, Star, Paperclip, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { useMailStore } from '../../store/mail';
import { useMessages, type MessageRow } from '../../hooks/useMessages';
import { useFolders } from '../../hooks/useFolders';

export function MailList() {
  const accountId = useMailStore((s) => s.selectedAccountId) ?? undefined;
  const folderId = useMailStore((s) => s.selectedFolderId) ?? undefined;
  const selectedMessageId = useMailStore((s) => s.selectedMessageId);
  const setSelectedMessage = useMailStore((s) => s.setSelectedMessage);
  const setAddAccountOpen = useMailStore((s) => s.setAddAccountOpen);

  const { data: folders } = useFolders(accountId);
  const folder = folders?.find((f) => f.id === folderId);

  const { data: messages, isLoading, refetch } = useMessages({
    account_id: accountId,
    folder_id: folderId,
  });

  const hasFolderSelected = !!folderId;

  return (
    <section className="flex w-[400px] flex-col border-r border-zinc-800/80 bg-zinc-950/20">
      <div className="flex h-10 items-center justify-between border-b border-zinc-800/80 px-3 text-xs">
        <div className="flex items-center gap-2">
          <Inbox size={12} className="text-zinc-500" />
          <span className="font-medium text-zinc-300">{folder?.name ?? 'Inbox'}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">{messages?.length ?? 0} messaggi</span>
        </div>
        <div className="flex items-center gap-1">
          <button className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300">
            <Filter size={12} />
          </button>
          <button
            onClick={() => refetch()}
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300"
          >
            <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!hasFolderSelected ? (
          <EmptyHint
            title="Nessun account collegato"
            description="Aggiungi il tuo primo account IMAP per iniziare a vedere le mail."
            cta="Aggiungi account"
            onClick={() => setAddAccountOpen(true)}
          />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={16} className="animate-spin text-zinc-600" />
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-8 py-16 text-center">
            <Inbox size={20} className="text-zinc-600" />
            <p className="mt-2 text-xs text-zinc-500">
              Nessun messaggio in questa cartella.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-900/80">
            {messages.map((m) => (
              <li
                key={m.id}
                onClick={() => setSelectedMessage(m.id)}
                className={`group cursor-pointer px-3 py-2.5 transition ${
                  selectedMessageId === m.id
                    ? 'bg-accent-500/10'
                    : m.is_seen
                      ? 'hover:bg-zinc-900/50'
                      : 'bg-zinc-900/30 hover:bg-zinc-900/60'
                }`}
              >
                <MessageRowView msg={m} active={selectedMessageId === m.id} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function MessageRowView({ msg, active }: { msg: MessageRow; active: boolean }) {
  const date = msg.received_at ?? msg.sent_at;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-2">
        <span
          className={`truncate text-sm ${
            msg.is_seen ? 'font-normal text-zinc-300' : 'font-semibold text-zinc-100'
          } ${active ? 'text-accent-200' : ''}`}
        >
          {msg.from_name ?? msg.from_address ?? '(sconosciuto)'}
        </span>
        <span className="shrink-0 text-[10px] text-zinc-500">
          {date ? formatDistanceToNow(new Date(date), { addSuffix: false, locale: it }) : ''}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        {msg.is_flagged && <Star size={10} className="text-amber-400" fill="currentColor" />}
        {msg.has_attachments && <Paperclip size={10} className="text-zinc-500" />}
        {msg.ai_priority === 'urgent' && (
          <span className="rounded bg-rose-500/20 px-1 text-[9px] font-semibold uppercase tracking-wider text-rose-300">
            urgent
          </span>
        )}
        {msg.ai_summary && <Sparkles size={9} className="text-accent-400" />}
        {msg.tags?.map((t) => (
          <span
            key={t.id}
            title={t.name}
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9px] font-medium text-zinc-200"
            style={{ backgroundColor: `${t.color}22`, color: t.color }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
            {t.name}
          </span>
        ))}
        <span
          className={`truncate text-xs ${
            msg.is_seen ? 'text-zinc-500' : 'text-zinc-300'
          }`}
        >
          {msg.subject ?? '(senza oggetto)'}
        </span>
      </div>
      {msg.snippet && (
        <span className="truncate text-[11px] text-zinc-500">{msg.snippet}</span>
      )}
    </div>
  );
}

function EmptyHint({
  title,
  description,
  cta,
  onClick,
}: {
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 py-16 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 ring-1 ring-zinc-800">
        <Inbox size={20} className="text-zinc-600" />
      </div>
      <h3 className="text-sm font-medium text-zinc-300">{title}</h3>
      <p className="mt-1 max-w-xs text-xs text-zinc-500">{description}</p>
      <button
        onClick={onClick}
        className="mt-4 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600"
      >
        + {cta}
      </button>
    </div>
  );
}
